import { makeStyles } from '@material-ui/core/styles'
import CustomField from 'elements/field/CustomField'
import DependentInput from 'elements/input/DependentInput'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { Grid } from 'opensolar-ui'
import { NumberInput, SelectInput, useTranslate } from 'react-admin'
import { validRange } from 'validations'
import {
  FieldWithTooltipContainer,
  FieldWrapper,
  SubsectionHeader,
  TooltipIconWrapper,
} from '../ProjectConfigurations.styles'
import { BatteryEndOfLifeDefinitionTooltip } from './BatteryEndOfLifeDefinitionTooltip'
import { BatteryReplacementModellingTooltip } from './BatteryReplacementModellingTooltip'
import { ReplacementCostMethodFields } from './ReplacementCostMethodFields'

const useTooltipStyles = makeStyles((theme) => ({
  tooltipWide: {
    maxWidth: '90vw',
    minWidth: '280px !important',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '600px !important',
      minWidth: '350px !important',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '700px !important',
      minWidth: '400px !important',
    },
  },
}))

interface BatteryReplacementSectionProps {
  allowEdit: boolean
  [key: string]: any
}

export const BatteryReplacementSection = ({ allowEdit, ...props }: BatteryReplacementSectionProps) => {
  const translate = useTranslate()
  const tooltipClasses = useTooltipStyles()

  return (
    <>
      <SubsectionHeader>{translate('Battery')}</SubsectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <FieldWithTooltipContainer>
            <FieldWrapper style={{ width: '100%' }}>
              <CustomField
                component={SelectInput}
                name={'end_of_life_config.end_of_life_definition'}
                source="end_of_life_config.end_of_life_definition"
                label="Battery End of Life Definition"
                resource="project_configurations"
                choices={[
                  { id: 0, name: 'Calendar & Cycle Count' },
                  { id: 1, name: 'Cycle Count Only' },
                  { id: 2, name: 'Calendar Only' },
                  { id: 3, name: 'End of Life Years Override' },
                ]}
                disabled={!allowEdit}
                defaultValue={1}
                fullWidth
              />
            </FieldWrapper>
            <TooltipIconWrapper>
              <InfoTooltip
                placement="right"
                title={<BatteryEndOfLifeDefinitionTooltip />}
                severity="info"
                size="small"
                classes={{
                  tooltip: tooltipClasses.tooltipWide,
                }}
              />
            </TooltipIconWrapper>
          </FieldWithTooltipContainer>
        </Grid>
        <DependentInput dependsOn="end_of_life_config.end_of_life_definition" resolve={(val) => val === 3}>
          <Grid item xs={12} sm={6} md={4}>
            <FieldWrapper style={{ width: '100%' }}>
              <NumberInput
                style={{ width: '100%' }}
                name="end_of_life_config.end_of_life_years_override"
                source="end_of_life_config.end_of_life_years_override"
                label="Battery End of Life Years Override"
                defaultValue={10}
                endAdornment={translate('years')}
                disabled={!allowEdit}
                InputLabelProps={{ shrink: true }}
                helperText={'Specify the number of years for battery end of life (1-25 years)'}
                validate={validRange(1, 50, true, { fieldName: 'Battery End of Life Years Override' })}
                fullWidth
              />
            </FieldWrapper>
          </Grid>
        </DependentInput>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <FieldWithTooltipContainer>
            <FieldWrapper style={{ width: '100%' }}>
              <CustomField
                component={SelectInput}
                name={'end_of_life_config.battery_replacement_modelling'}
                source="end_of_life_config.battery_replacement_modelling"
                label="Battery Replacement Modelling"
                resource="project_configurations"
                choices={[
                  { id: 0, name: 'Remove' },
                  { id: 1, name: 'Replace' },
                  { id: 2, name: 'Extend Beyond Warranty' },
                ]}
                disabled={!allowEdit}
                fullWidth
                defaultValue={0}
              />
            </FieldWrapper>
            <TooltipIconWrapper>
              <InfoTooltip
                placement="right"
                title={<BatteryReplacementModellingTooltip />}
                severity="info"
                size="small"
                classes={{
                  tooltip: tooltipClasses.tooltipWide,
                }}
              />
            </TooltipIconWrapper>
          </FieldWithTooltipContainer>
        </Grid>
      </Grid>
      <DependentInput dependsOn="end_of_life_config.battery_replacement_modelling" resolve={(val) => val === 1}>
        <ReplacementCostMethodFields
          componentType="battery"
          costMethodField="battery_replacement_cost_method"
          inflationField="battery_replacement_cost_inflation_annual"
          fixedValueField="battery_replacement_cost_value"
          allowEdit={allowEdit}
          {...props}
        />
      </DependentInput>
    </>
  )
}
